import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { SourceHealth } from '@mg/shared';
import { useTenant } from '../../auth/TenantContext';
import { api } from '../../lib/api';
import { useTenantKey } from '../../lib/useTenantKey';
import { folderCounts } from '../../features/messages/seed-data';
import type { NavBadges } from './nav';

/**
 * The counts on the nav's badges, from the same queries (and so the same cache) the Messages, Sources
 * and Dashboard pages read: unread conversations, and Digest Sources whose pulls are failing.
 *
 * The inbox is NOT asked for here. It is read live from Meta, several calls for every connected
 * account, and the shell is on every page: the badge only reads what the Dashboard or the Messages page
 * loaded (`enabled: false` still reads the cache and follows every update to it). Until one of them
 * has, there is no Messages count. The sources list is a database read, so the shell asks for it.
 */
export function useNavBadges(): NavBadges {
  const { hasWorkspace } = useTenant();
  const tenantKey = useTenantKey();

  const inbox = useQuery({
    queryKey: ['inbox', tenantKey],
    queryFn: ({ signal }) => api.getInboxConversations(signal),
    enabled: false,
  });
  const sources = useQuery({
    queryKey: ['sources', tenantKey],
    queryFn: ({ signal }) => api.listSources(signal),
    enabled: hasWorkspace && tenantKey !== '',
  });

  return useMemo(
    () => ({
      messages: inbox.data ? folderCounts(inbox.data).unread : 0,
      sources: (sources.data ?? []).filter(
        (s) => s.health === SourceHealth.DEGRADED || s.health === SourceHealth.FAILING,
      ).length,
    }),
    [inbox.data, sources.data],
  );
}

/** A badge's count as printed: past 99 it is "99+", so it stays a small pill. */
export const badgeText = (count: number) => (count > 99 ? '99+' : String(count));

/** What a screen reader hears after the item's name while it has a count. */
export const badgeWords = (kind: keyof NavBadges, count: number) =>
  kind === 'messages' ? `${count} unread` : `${count} need attention`;
